export const DUNGEON_META_VERSION = 1 as const;
export const DUNGEON_FLOOR_CASH_COST = 199;
export const DUNGEON_FLOOR_SKU = "dungeon.floor_slot.v1";
export const DUNGEON_MAX_FLOORS_CAP = 50;
export const DUNGEON_FREE_FLOORS = 1;

export type DungeonMeta = {
  version: typeof DUNGEON_META_VERSION;
  maxFloors: number;
  published: boolean;
};

const STORAGE_PREFIX = "mind-survival/dungeon-meta/v1";

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

export function createDefaultDungeonMeta(): DungeonMeta {
  return {
    version: DUNGEON_META_VERSION,
    maxFloors: DUNGEON_FREE_FLOORS,
    published: false,
  };
}

export function normalizeDungeonMeta(raw: unknown): DungeonMeta {
  const base = createDefaultDungeonMeta();
  if (!raw || typeof raw !== "object") return base;
  const p = raw as Partial<DungeonMeta>;
  const maxFloors =
    typeof p.maxFloors === "number" && Number.isFinite(p.maxFloors)
      ? Math.min(DUNGEON_MAX_FLOORS_CAP, Math.max(DUNGEON_FREE_FLOORS, Math.floor(p.maxFloors)))
      : base.maxFloors;
  return {
    version: DUNGEON_META_VERSION,
    maxFloors,
    published: p.published === true,
  };
}

export function loadDungeonMeta(userId: string): DungeonMeta {
  const raw = localStorage.getItem(storageKey(userId));
  if (!raw) return createDefaultDungeonMeta();
  try {
    return normalizeDungeonMeta(JSON.parse(raw));
  } catch {
    localStorage.removeItem(storageKey(userId));
    return createDefaultDungeonMeta();
  }
}

export function saveDungeonMeta(userId: string, meta: DungeonMeta): void {
  localStorage.setItem(storageKey(userId), JSON.stringify(meta));
  import("@/cloud/player-cloud-scheduler").then((m) =>
    m.schedulePlayerCloudPush(userId, "gameState"),
  );
}

export function grantDungeonFloorSlots(meta: DungeonMeta, count: number): DungeonMeta | null {
  const n = Math.max(0, Math.floor(count));
  if (n === 0) return meta;
  if (meta.maxFloors >= DUNGEON_MAX_FLOORS_CAP) return null;
  return {
    ...meta,
    maxFloors: Math.min(DUNGEON_MAX_FLOORS_CAP, meta.maxFloors + n),
  };
}

export function dungeonSceneId(floor: number): string {
  return `dungeon-floor-${Math.max(1, Math.floor(floor))}`;
}
